import { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { roundMoney, multiplyMoney, toCents, fromCents } from '@/lib/money';
import { getPreferredDefaultTaxRate, parseTaxRateValue } from '@/lib/finance/taxRates';
import {
  type DocumentFormState,
  type HydrationSourceLineItem,
} from '../documentModalHelpers';
import {
  type DocumentLineItem,
  type LineItemType,
} from '@/hooks/useDocumentsWorkItems';
import type { FinanceSummaryRow } from '../DocumentModalShared';
import type { Contact } from '@/app/client-zone/modules/total-finance/hooks/useContacts';
import type { Document } from '@/hooks/useDocumentsWorkItems';
import type { DocumentConfig } from '../documentModalHelpers';
import type { DocumentTaxRate } from '../SortableLineItem';

const CURRENCY_CONFIG: Record<string, { symbol: string; name: string; locale: string; flag: string }> = {
  ZAR: { symbol: 'R', name: 'South African Rand', locale: 'en-ZA', flag: '🇿🇦' },
  USD: { symbol: '$', name: 'US Dollar', locale: 'en-US', flag: '🇺🇸' },
  EUR: { symbol: '€', name: 'Euro', locale: 'de-DE', flag: '🇪🇺' },
  GBP: { symbol: '£', name: 'British Pound', locale: 'en-GB', flag: '🇬🇧' },
  BWP: { symbol: 'P', name: 'Botswana Pula', locale: 'en-BW', flag: '🇧🇼' },
  NAD: { symbol: 'N$', name: 'Namibian Dollar', locale: 'en-NA', flag: '🇳🇦' },
  MZN: { symbol: 'MT', name: 'Mozambican Metical', locale: 'pt-MZ', flag: '🇲🇿' },
  MWK: { symbol: 'MK', name: 'Malawian Kwacha', locale: 'en-MW', flag: '🇲🇼' },
  ZMW: { symbol: 'ZK', name: 'Zambian Kwacha', locale: 'en-ZM', flag: '🇿🇲' },
  KES: { symbol: 'KSh', name: 'Kenyan Shilling', locale: 'en-KE', flag: '🇰🇪' },
  NGN: { symbol: '₦', name: 'Nigerian Naira', locale: 'en-NG', flag: '🇳🇬' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', locale: 'en-AU', flag: '🇦🇺' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', locale: 'en-CA', flag: '🇨🇦' },
  CHF: { symbol: 'CHF', name: 'Swiss Franc', locale: 'de-CH', flag: '🇨🇭' },
  INR: { symbol: '₹', name: 'Indian Rupee', locale: 'en-IN', flag: '🇮🇳' },
  AED: { symbol: 'د.إ', name: 'UAE Dirham', locale: 'ar-AE', flag: '🇦🇪' },
  CNY: { symbol: '¥', name: 'Chinese Yuan', locale: 'zh-CN', flag: '🇨🇳' },
  JPY: { symbol: '¥', name: 'Japanese Yen', locale: 'ja-JP', flag: '🇯🇵' },
};

interface UseDocumentTotalsParams {
  formData: DocumentFormState;
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormState>>;
  items: DocumentLineItem[];
  document: Document | null;
  config: DocumentConfig;
  taxRates: DocumentTaxRate[];
  selectedContact: Contact | null;
  settingsDefaultTaxRateId: number | undefined;
  // Expense-side documents (din/expense/supplier_invoice): VAT comes from the
  // supplier's receipt/invoice — the seller's VAT status and product type
  // determine the rate, not the client's tax_rates table. When true, stored
  // per-line tax_rate/tax_amount/line_total are preserved verbatim instead of
  // being re-derived from (or zeroed by a missing match in) client tax rates.
  preserveSourceTax?: boolean;
  // Total-lock reconciliation is a DIN-only concept — the locked OCR slip
  // total that line items must reconcile to. When false, totalLocked can
  // never engage and no discrepancy is ever reported. Defaults to true so
  // other callers keep current behaviour.
  totalLockAvailable?: boolean;
}

export function useDocumentTotals({
  formData,
  setFormData,
  items,
  document: doc,
  config,
  taxRates,
  selectedContact,
  settingsDefaultTaxRateId,
  preserveSourceTax = false,
  totalLockAvailable = true,
}: UseDocumentTotalsParams) {
  // Track whether the user has manually modified line items.
  // Until the first manual edit, we preserve the stored document totals
  // (which may differ from line-item-derived totals for DIN/expense docs
  // where extraction tax rates are being corrected by the user).
  const itemsModifiedRef = useRef(false);
  const prevItemsRef = useRef<DocumentLineItem[]>(items);
  const docIdRef = useRef<string | number | undefined>(doc?.id);

  // ── Total Lock Mode ──
  // When locked, the grand_total from the OCR extraction is preserved as the
  // source of truth. Line items must reconcile to it (via a balancing line if
  // needed). When unlocked, the grand_total is computed from line items.
  const [totalLocked, setTotalLocked] = useState(false);
  const prevDocIdRef = useRef<string | number | undefined>(undefined);

  // Auto-lock when a new document with OCR extraction data is loaded
  useEffect(() => {
    const docId = doc?.id;
    if (docId !== prevDocIdRef.current) {
      prevDocIdRef.current = docId;
      // Auto-lock if the document has an OCR-extracted total (extraction_payload or receipt_ai_validation)
      const cf = (doc?.custom_fields as Record<string, unknown> | undefined) || {};
      const hasOcrExtraction = Boolean(
        (cf.extraction_payload && typeof cf.extraction_payload === 'object') ||
        (cf.receipt_ai_validation && typeof cf.receipt_ai_validation === 'object') ||
        (cf.extraction && typeof cf.extraction === 'object')
      );
      const hasGrandTotal = (Number(doc?.grand_total) || 0) > 0.01;
      setTotalLocked(totalLockAvailable && hasOcrExtraction && hasGrandTotal);
    }
  }, [doc?.id, doc?.custom_fields, doc?.grand_total, totalLockAvailable]);

  const toggleTotalLock = useCallback(() => {
    setTotalLocked(prev => !prev);
  }, []);

  const activeCurrency = formData.currency || 'ZAR';
  const currencySymbol = CURRENCY_CONFIG[activeCurrency]?.symbol || activeCurrency;

  const formatCurrency = useCallback((value: number | string | undefined | null): string => {
    if (value === null || value === undefined) return '0.00';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '0.00';
    // Use the currency's locale for proper grouping/decimal separators.
    // Fall back to en-ZA if the currency code is invalid.
    const locale = CURRENCY_CONFIG[activeCurrency]?.locale || 'en-ZA';
    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numValue);
    } catch {
      return new Intl.NumberFormat('en-ZA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numValue);
    }
  }, [activeCurrency]);

  const formatCurrencyWithSymbol = useCallback((value: number | string | undefined | null): string => {
    return `${currencySymbol} ${formatCurrency(value)}`;
  }, [currencySymbol, formatCurrency]);

  const preferredDefaultTaxRate = useMemo(() => {
    if (settingsDefaultTaxRateId) {
      const settingsTaxRate = taxRates.find(rate => rate.id === settingsDefaultTaxRateId);
      if (settingsTaxRate) {
        return settingsTaxRate;
      }
    }
    if (selectedContact?.tax_rate_id) {
      const contactTaxRate = taxRates.find(rate => rate.id === selectedContact.tax_rate_id);
      if (contactTaxRate) {
        return contactTaxRate;
      }
    }
    return getPreferredDefaultTaxRate(taxRates);
  }, [taxRates, selectedContact, settingsDefaultTaxRateId]);

  const hydrateDocumentItemsBase = useCallback((sourceItems: Partial<DocumentLineItem>[] = []): DocumentLineItem[] => (
    sourceItems.map((item: HydrationSourceLineItem) => ({
      ...item,
      // Map database column `account_code` to the frontend's `expense_account_code`
      // so that GL allocation displays and editing work correctly for line items
      // loaded from the work_item_bom_items API.
      expense_account_code: (item as HydrationSourceLineItem).expense_account_code
        || (item as HydrationSourceLineItem).account_code
        || undefined,
      description: item.description || item.item_name || '',
      notes: item.notes || '',
      quantity: parseFloat(String(item.quantity || 0)) || 0,
      unit: item.unit || item.unit_of_measure || 'each',
      unit_price: parseFloat(String(item.unit_price ?? item.unit_cost ?? 0)) || 0,
      tax_rate: parseFloat(String(item.tax_rate || 0)) || 0,
      tax_rate_id: item.tax_rate_id != null ? Number(item.tax_rate_id) : undefined,
      tax_amount: parseFloat(String(item.tax_amount || 0)) || 0,
      discount_amount: parseFloat(String(item.discount_amount || 0)) || 0,
      discount_percentage: parseFloat(String(item.discount_percentage || 0)) || 0,
      markup_percentage: parseFloat(String(item.markup_percentage || 0)) || 0,
      line_total: parseFloat(String(item.line_total ?? item.total_cost ?? 0)) || 0,
      line_type: (item.line_type || 'item') as LineItemType,
      is_optional: Boolean(item.is_optional),
      is_required: Boolean(item.is_required),
      quantity_editable: item.quantity_editable ?? true,
      price_editable: item.price_editable ?? true,
      source_bundle_id: item.source_bundle_id,
      source_bundle_name: item.source_bundle_name,
      purchase_supplier_name: item.purchase_supplier_name,
      purchase_supplier_contact_id: item.purchase_supplier_contact_id,
      job_number: item.job_number || null,
      appointment_number: item.appointment_number || null,
    }))
  ), []);

  const normalizeDocumentItems = useCallback((sourceItems: Partial<DocumentLineItem>[] = []): DocumentLineItem[] => {
    return hydrateDocumentItemsBase(sourceItems).map((parsedItem) => {
      const sourceItem = parsedItem as DocumentLineItem & { tax_rate_id?: number | null };

      const isProductiveLine = parsedItem.line_type === 'item' || parsedItem.line_type === 'expense_line' || parsedItem.line_type === 'service' || parsedItem.line_type === 'expense' || parsedItem.line_type === 'tip';

      // Expense documents: the supplier's document is the source of truth for
      // VAT. Keep the stored tax_rate/tax_amount/line_total verbatim — never
      // zero the rate because the client's tax_rates table lacks a match and
      // never recompute the amount (re-deriving drifts, e.g. 108.70 × 15% →
      // 16.31 vs the receipt's printed 16.30). Only resolve a tax_rate_id so
      // the rate dropdown can render the value.
      if (preserveSourceTax) {
        const displayRate = taxRates.find((rate) =>
          (sourceItem.tax_rate_id != null && rate.id === sourceItem.tax_rate_id)
          || parseTaxRateValue(rate.rate) === parseTaxRateValue(parsedItem.tax_rate));
        return {
          ...parsedItem,
          tax_rate_id: displayRate?.id ?? sourceItem.tax_rate_id ?? undefined,
        };
      }

      // Determine the applicable tax rate, falling back to the preferred default for item lines.
      let taxRateObj: DocumentTaxRate | undefined;

      const matchingRate = taxRates.find((rate) => {
        if (sourceItem.tax_rate_id && rate.id === sourceItem.tax_rate_id) {
          return true;
        }
        return parseTaxRateValue(rate.rate) === parseTaxRateValue(parsedItem.tax_rate);
      });

      if (matchingRate) {
        taxRateObj = matchingRate;
      } else if (parseTaxRateValue(parsedItem.tax_rate)) {
        // A numeric rate exists but is not yet matched to a tax_rate_id.
        taxRateObj = { id: sourceItem.tax_rate_id ?? 0, rate: parseTaxRateValue(parsedItem.tax_rate) } as DocumentTaxRate;
      }
      // NOTE: We no longer auto-assign the default tax rate when blank.
      // A blank tax dropdown means no tax (0%) — the user must explicitly select a rate.

      // Non-productive rows (header, comment, spacer, subtotal) keep their stored totals.
      if (!isProductiveLine || !taxRateObj) {
        return {
          ...parsedItem,
          tax_rate: isProductiveLine ? 0 : parsedItem.tax_rate,
          tax_rate_id: isProductiveLine ? undefined : sourceItem.tax_rate_id,
          tax_amount: isProductiveLine ? 0 : parsedItem.tax_amount,
          line_total: parsedItem.line_total,
        };
      }

      const lineSubtotal = multiplyMoney(parsedItem.quantity || 0, parsedItem.unit_price || 0);
      const discountAmount = roundMoney(parsedItem.discount_amount || 0);
      const lineAfterDiscount = roundMoney(lineSubtotal - discountAmount);
      const markupPct = Number(parsedItem.markup_percentage || 0);
      const markupAmount = markupPct > 0 ? roundMoney(lineAfterDiscount * (markupPct / 100)) : 0;
      const lineAfterMarkup = roundMoney(lineAfterDiscount + markupAmount);

      const taxRate = parseTaxRateValue(taxRateObj.rate) || 0;
      let taxAmount = 0;
      let lineTotal = lineAfterMarkup;
      if (taxRate > 0) {
        if (parsedItem.tax_inclusive) {
          const taxDivisor = 1 + taxRate / 100;
          taxAmount = roundMoney(lineAfterMarkup - (lineAfterMarkup / taxDivisor));
          lineTotal = roundMoney(lineAfterMarkup - taxAmount);
        } else {
          taxAmount = roundMoney((lineAfterMarkup * taxRate) / 100);
          // Line total is ex-VAT; tax is added at the document summary.
          lineTotal = lineAfterMarkup;
        }
      }

      return {
        ...parsedItem,
        tax_rate: taxRate,
        tax_rate_id: taxRateObj.id || sourceItem.tax_rate_id,
        tax_amount: taxAmount,
        line_total: lineTotal,
      };
    });
  }, [hydrateDocumentItemsBase, taxRates, preserveSourceTax]);

  // ============================================================================
  // CLIENT-SIDE TOTALS — computed directly from line items via useMemo.
  // This ensures the summary block always reflects the current line item state,
  // even before the API-based calculateTotals round-trip completes.
  // The logic mirrors SortableLineItem's preTaxTotal / totalWithTax computation.
  // ============================================================================
  const computedTotals = useMemo(() => {
    // Accumulate in integer minor units (cents) to avoid floating-point drift.
    // The final totals are converted back to decimals via fromCents() at the end.
    let subtotalCents = 0;   // sum of pre-tax line amounts
    let taxTotalCents = 0;   // sum of per-line tax amounts
    let grandTotalCents = 0;  // sum of line totals (pre-tax + tax)

    for (const item of items) {
      const lt = item.line_type || 'item';
      // Skip non-productive rows (header, comment, spacer, subtotal)
      if (lt === 'header' || lt === 'comment' || lt === 'spacer' || lt === 'subtotal') continue;

      const qty = Number(item.quantity) || 0;
      const unitPrice = Number(item.unit_price) || 0;
      const lineSubtotal = multiplyMoney(qty, unitPrice);
      const discountAmount = roundMoney(Number(item.discount_amount) || 0);
      const lineAfterDiscount = roundMoney(lineSubtotal - discountAmount);
      const markupPct = Number(item.markup_percentage) || 0;
      const markupAmount = markupPct > 0 ? roundMoney(lineAfterDiscount * (markupPct / 100)) : 0;
      const lineAfterMarkup = roundMoney(lineAfterDiscount + markupAmount);

      // For expense documents the stored per-line tax_amount comes from the
      // supplier's document and is authoritative — re-deriving it from the
      // rate drifts by a cent (108.70 × 15% = 16.305 → 16.31 vs printed 16.30).
      const storedTaxAmount = preserveSourceTax && item.tax_amount != null
        ? Number(item.tax_amount)
        : null;
      // Always compute tax from tax_rate — never from stored tax_amount.
      // This ensures the summary matches the line item display exactly.
      const taxRate = Number(item.tax_rate) || 0;
      let lineTaxAmount = storedTaxAmount ?? 0;
      let preTax = lineAfterMarkup;

      if (storedTaxAmount == null && taxRate > 0) {
        if (item.tax_inclusive) {
          const taxDivisor = 1 + taxRate / 100;
          lineTaxAmount = roundMoney(lineAfterMarkup - (lineAfterMarkup / taxDivisor));
          preTax = roundMoney(lineAfterMarkup - lineTaxAmount);
        } else {
          lineTaxAmount = roundMoney((lineAfterMarkup * taxRate) / 100);
          preTax = lineAfterMarkup;
        }
      } else if (item.tax_inclusive) {
        // Stored tax on an inclusive line still needs the net split.
        preTax = roundMoney(lineAfterMarkup - lineTaxAmount);
      }
      // When taxRate is 0: lineTaxAmount = 0, preTax = lineAfterMarkup (same as total)

      // Accumulate in cents to prevent float drift across many line items
      subtotalCents += toCents(preTax);
      taxTotalCents += toCents(lineTaxAmount);
      grandTotalCents += toCents(preTax) + toCents(lineTaxAmount);
    }

    return {
      subtotal: fromCents(subtotalCents),
      taxTotal: fromCents(taxTotalCents),
      grandTotal: fromCents(grandTotalCents),
    };
  }, [items, preserveSourceTax]);

  // Detect when items are modified by the user (not just loaded from a document).
  // We compare the current items array reference to the previous one — if it changed
  // and the document hasn't just been loaded, the user has edited something.
  useEffect(() => {
    // Reset the modified flag when a new document is loaded
    if (doc?.id !== docIdRef.current) {
      docIdRef.current = doc?.id;
      itemsModifiedRef.current = false;
      prevItemsRef.current = items;
      return;
    }
    // If the items reference changed (not just initial load), mark as modified
    if (items !== prevItemsRef.current && prevItemsRef.current.length > 0) {
      itemsModifiedRef.current = true;
    }
    prevItemsRef.current = items;
  }, [items, doc?.id]);

  // Sync computed totals to formData so they persist on save.
  // Only do this after the user has manually modified a line item —
  // for existing documents, preserve the stored totals until the user edits.
  // When totalLocked is true, DON'T overwrite the locked grand_total — the
  // OCR-extracted total is the source of truth and line items must reconcile to it.
  useEffect(() => {
    if (!itemsModifiedRef.current) return;
    if (totalLocked) return; // Preserve locked total

    setFormData(prev => {
      const shippingCost = roundMoney(Number(prev.shipping_cost) || 0);
      const roundingAdjustment = Number(prev.rounding_adjustment) || 0;
      const newGrandTotal = roundMoney(computedTotals.grandTotal + shippingCost + roundingAdjustment);

      // Avoid unnecessary updates
      if (
        prev.subtotal === computedTotals.subtotal &&
        prev.tax_total === computedTotals.taxTotal &&
        prev.grand_total === newGrandTotal
      ) return prev;

      return {
        ...prev,
        subtotal: computedTotals.subtotal,
        tax_total: computedTotals.taxTotal,
        grand_total: newGrandTotal,
        balance_due: roundMoney(newGrandTotal - (Number(prev.amount_paid) || 0)),
      };
    });
  }, [computedTotals, setFormData, totalLocked]);

  // For the summary display: use computed totals once the user has modified line items,
  // otherwise use the stored formData totals (which match ExpenseInbox for existing docs).
  // When totalLocked is true, always show the locked formData totals (OCR source of truth).
  const displayTotals = totalLocked
    ? {
        subtotal: roundMoney(Number(formData.subtotal) || 0),
        taxTotal: roundMoney(Number(formData.tax_total) || 0),
        grandTotal: roundMoney(Number(formData.grand_total) || 0),
      }
    : itemsModifiedRef.current
      ? computedTotals
      : {
          subtotal: roundMoney(Number(formData.subtotal) || 0),
          taxTotal: roundMoney(Number(formData.tax_total) || 0),
          grandTotal: roundMoney(Number(formData.grand_total) || 0),
        };

  // ── Discrepancy Detection ──
  // When locked, compare the locked grand_total to the sum of line items.
  // A non-zero discrepancy means the line items don't reconcile to the slip total.
  const lineItemsSum = computedTotals.grandTotal;
  const lockedTotal = roundMoney(Number(formData.grand_total) || 0);
  const discrepancy = totalLocked
    ? roundMoney(lockedTotal - lineItemsSum)
    : 0;
  const hasDiscrepancy = Math.abs(discrepancy) > 0.02;

  const summaryRows: FinanceSummaryRow[] = [
    {
      label: 'Subtotal',
      value: formatCurrencyWithSymbol(displayTotals.subtotal),
    },
    ...(formData.discount_amount && formData.discount_amount > 0
      ? [{
        label: 'Discount',
        value: `-${formatCurrencyWithSymbol(formData.discount_amount)}`,
        tone: 'warning' as const,
      }]
      : []),
    {
      label: 'Tax',
      value: formatCurrencyWithSymbol(displayTotals.taxTotal),
    },
    ...(config.fields.showShippingCost && formData.shipping_cost != null && formData.shipping_cost > 0
      ? [{
        label: 'Shipping',
        value: formatCurrencyWithSymbol(formData.shipping_cost),
      }]
      : []),
    ...(formData.rounding_adjustment != null && Math.abs(Number(formData.rounding_adjustment)) > 0.001
      ? [{
        label: 'Round-off',
        value: formatCurrencyWithSymbol(Number(formData.rounding_adjustment)),
        tone: 'muted' as const,
      }]
      : []),
    // When locked and line items don't sum to the slip total, show the discrepancy
    ...(hasDiscrepancy
      ? [{
          label: 'Line items sum',
          value: formatCurrencyWithSymbol(lineItemsSum),
          tone: 'muted' as const,
        }, {
          label: discrepancy > 0 ? 'Balancing line needed' : 'Surplus (over-allocated)',
          value: (discrepancy > 0 ? '+' : '') + formatCurrencyWithSymbol(discrepancy),
          tone: 'danger' as const,
        }]
      : []),
  ];

  const summaryFooterRows: FinanceSummaryRow[] = [
    ...(activeCurrency !== 'ZAR' && formData.exchange_rate && formData.grand_total
      ? [{
        label: 'ZAR Equivalent',
        value: `≈ R ${formatCurrency(formData.grand_total * formData.exchange_rate)}`,
        tone: 'muted' as const,
      }]
      : []),
    ...(config.fields.showAmountPaid
      ? [{
        label: 'Amount Paid',
        value: formatCurrencyWithSymbol(formData.amount_paid ?? 0),
        tone: 'default' as const,
      }, {
        label: 'Balance Due',
        value: formatCurrencyWithSymbol(formData.balance_due ?? formData.grand_total ?? 0),
        tone: (formData.balance_due ?? 0) > 0 ? 'danger' as const : 'positive' as const,
      }]
      : []),
  ];

  // NOTE: The old API-based recalculation (calculateTotals) has been replaced by
  // the client-side computedTotals useMemo above. This ensures the summary is
  // always derived directly from the line items without a network round-trip,
  // and correctly handles tax_inclusive line items and 0% tax rates.

  // Recalculate grand_total when shipping_cost changes
  useEffect(() => {
    setFormData(prev => {
      const shippingCost = roundMoney(Number(prev.shipping_cost) || 0);
      const roundingAdjustment = Number(prev.rounding_adjustment) || 0;
      const baseTotal = roundMoney((Number(prev.subtotal) || 0) + (Number(prev.tax_total) || 0));
      const grandTotal = roundMoney(baseTotal + shippingCost + roundingAdjustment);
      if (grandTotal === prev.grand_total) return prev;
      return {
        ...prev,
        grand_total: grandTotal,
        balance_due: roundMoney(grandTotal - (Number(prev.amount_paid) || 0))
      };
    });
  }, [formData.shipping_cost, setFormData]);

  return {
    CURRENCY_CONFIG,
    activeCurrency,
    currencySymbol,
    formatCurrency,
    formatCurrencyWithSymbol,
    preferredDefaultTaxRate,
    hydrateDocumentItemsBase,
    normalizeDocumentItems,
    summaryRows,
    summaryFooterRows,
    // Total lock mode
    totalLocked,
    toggleTotalLock,
    computedTotals,
    discrepancy,
    hasDiscrepancy,
    lineItemsSum,
  };
}
